import { getRuntimeConfig } from "../Config/RuntimeConfig";
import { ensureGtag, loadGtagScript } from "./gtagLoader";

// Campaign Manager 360 Floodlight (Google "DC-" tag) for etihadrail.ae.
// Activity ids and custom variables come from the marketing sheet
// "GTM - Google Floodlights" (advertiser 16849973). Non-secret.
//
// Floodlight is an ADVERTISING tag: it is enabled only once the user grants
// the `marketing` cookie category (CookieConsentUtils), never on `statistic`
// alone. Events raised before that are dropped, except the current page view,
// which is kept and replayed so the page the user consented on is counted.
//
// The <noscript> image pixels from the sheet are intentionally not shipped:
// this is a client-rendered app, so they would never fire.
export const FLOODLIGHT_ID = "DC-16849973";

const GROUP_STANDARD = "etiha0";
const GROUP_SALES = "etiha00";

export const FloodlightActivity = {
  allPages: `${FLOODLIGHT_ID}/${GROUP_STANDARD}/all-p0+standard`,
  homePage: `${FLOODLIGHT_ID}/${GROUP_STANDARD}/home-0+standard`,
  searchButton: `${FLOODLIGHT_ID}/${GROUP_STANDARD}/searc0+standard`,
  trainOfferPage: `${FLOODLIGHT_ID}/${GROUP_STANDARD}/train0+standard`,
  checkoutPage: `${FLOODLIGHT_ID}/${GROUP_STANDARD}/check0+standard`,
  purchase: `${FLOODLIGHT_ID}/${GROUP_SALES}/thank0+transactions`,
} as const;

let _enabled = false;
let _configured = false;
let _initPromise: Promise<boolean> | null = null;
let _lastPath: string | null = null;
let _pendingPath: string | null = null;

/**
 * Enable Floodlight. Idempotent. MUST only be called once the user has granted
 * `marketing` consent (callers enforce this). Returns false on the server or
 * when NEXT_PUBLIC_ENABLE_FLOODLIGHT is not "true" for the environment.
 */
export async function initFloodlight(): Promise<boolean> {
  if (_enabled) return true;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return false;
      }
      const config = await getRuntimeConfig();
      if (config?.NEXT_PUBLIC_ENABLE_FLOODLIGHT !== "true") return false;

      if (!_configured) {
        const gtag = ensureGtag();
        loadGtagScript(FLOODLIGHT_ID);
        gtag("js", new Date());
        gtag("config", FLOODLIGHT_ID);
        _configured = true;
      }
      _enabled = true;

      if (_pendingPath !== null) {
        const path = _pendingPath;
        _pendingPath = null;
        firePageActivities(path);
      }
      return true;
    } catch (error) {
      console.error("Floodlight init failed:", error);
      return false;
    } finally {
      _initPromise = null;
    }
  })();

  return _initPromise;
}

/** Stop sending Floodlight events (marketing consent withdrawn). */
export function disableFloodlight(): void {
  _enabled = false;
}

export function isFloodlightEnabled(): boolean {
  return _enabled;
}

/**
 * Page-reach activities for a route. The checkout activity is NOT here: it
 * carries the booking total, so the checkout page fires it itself once the
 * booking is loaded (see app/checkout/page.tsx).
 */
export function pageActivitiesFor(pathname: string): string[] {
  const activities: string[] = [FloodlightActivity.allPages];
  if (pathname === "/") activities.push(FloodlightActivity.homePage);
  // Covers /train-offer and the after-sales variants
  // (/book-return-journey/train-offer, /bookings/change-date/train-offer).
  if (pathname === "/train-offer" || pathname.endsWith("/train-offer")) {
    activities.push(FloodlightActivity.trainOfferPage);
  }
  return activities;
}

/** Floodlight custom variables are strings; drop empty ones rather than send "". */
const clean = (p: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(p).filter(([, v]) => v !== null && v !== undefined && v !== ""),
  );

function commonParams(): Record<string, unknown> {
  return {
    allow_custom_scripts: true,
    u1: window.location.href, // [url]
    u2: document.documentElement.lang || "", // [Language]
  };
}

function send(
  eventName: "conversion" | "purchase",
  sendTo: string,
  params: Record<string, unknown>,
): void {
  if (!_enabled) return;
  try {
    ensureGtag()("event", eventName, clean({ ...commonParams(), ...params, send_to: sendTo }));
  } catch (error) {
    console.error("Floodlight event failed:", error);
  }
}

function firePageActivities(pathname: string): void {
  for (const activity of pageActivitiesFor(pathname)) {
    send("conversion", activity, {});
  }
}

export const Floodlight = {
  /**
   * Route change. Fires All-Pages plus Home-page / Train-offer-page when the
   * path matches. Before consent the latest path is remembered and replayed
   * on init. Consecutive identical paths are ignored (React strict mode
   * double effects, param-only changes).
   */
  pageView(pathname: string): void {
    if (!pathname || pathname === _lastPath) return;
    _lastPath = pathname;
    if (!_enabled) {
      _pendingPath = pathname;
      return;
    }
    firePageActivities(pathname);
  },

  /** Search-button activity. u3 = [Station Type], u4 = [Passenger Type]. */
  searchButton(o: { origin: string; destination: string; passengerType: string }): void {
    send("conversion", FloodlightActivity.searchButton, {
      u3: [o.origin, o.destination].filter(Boolean).join("-"),
      u4: o.passengerType,
    });
  },

  /** Check-out-page activity, fired once the booking total is known. u6 = [Total Revenue]. */
  checkoutPage(o: { totalRevenue: number }): void {
    send("conversion", FloodlightActivity.checkoutPage, {
      u6: String(o.totalRevenue),
    });
  },

  /**
   * Sales activity for a completed transaction. `transaction_id` doubles as
   * the Floodlight `ord` de-duplication key, so pass the booking reference.
   */
  purchase(o: { orderId: string; revenue: number }): void {
    send("purchase", FloodlightActivity.purchase, {
      value: o.revenue,
      transaction_id: o.orderId,
      u6: String(o.revenue),
    });
  },
};
